/**
 * Build a fully-qualified consumer-app URL with optional query params.
 *
 * Used by the marketing site to link to the consumer app at
 * ${VITE_CONSUMER_URL}/<path>?<params>. Marketing has no shared runtime
 * with consumer — only URL-based linking.
 *
 * @param baseUrl  e.g. "https://app.usesill.com" or "http://localhost:5173"
 * @param path     e.g. "/onboarding" or "" for root
 * @param params   optional flat object of query params; values are URL-encoded; keys are sorted for stable output
 */
export function getConsumerUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, string>,
): string {
  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const trimmedPath = path === "" ? "" : path.startsWith("/") ? path : `/${path}`;
  let url = `${trimmedBase}${trimmedPath}`;

  if (params && Object.keys(params).length > 0) {
    const search = new URLSearchParams();
    for (const key of Object.keys(params).sort()) {
      search.set(key, params[key]!);
    }
    url += `?${search.toString()}`;
  }

  return url;
}

/**
 * Build a fully-qualified pro-app (agent CRM) URL with optional query params.
 *
 * Symmetric with getConsumerUrl. Used by the consumer app to link to the pro
 * app, which lives on a separate origin (${VITE_PRO_URL}) with no shared
 * runtime — only URL-based linking. The pro app handles its own auth routing
 * (a cross-origin Clerk session may not carry over, so the landing path lands
 * on the pro login before forwarding to the dashboard).
 */
export function getProUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, string>,
): string {
  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const trimmedPath = path === "" ? "" : path.startsWith("/") ? path : `/${path}`;
  let url = `${trimmedBase}${trimmedPath}`;

  if (params && Object.keys(params).length > 0) {
    const search = new URLSearchParams();
    for (const key of Object.keys(params).sort()) {
      search.set(key, params[key]!);
    }
    url += `?${search.toString()}`;
  }

  return url;
}

/**
 * Build a fully-qualified marketing-site URL with optional query params.
 *
 * Symmetric with getConsumerUrl. Used by the consumer app to link back to
 * marketing pages (help articles, privacy, marketing home).
 */
export function getMarketingUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, string>,
): string {
  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const trimmedPath = path === "" ? "" : path.startsWith("/") ? path : `/${path}`;
  let url = `${trimmedBase}${trimmedPath}`;

  if (params && Object.keys(params).length > 0) {
    const search = new URLSearchParams();
    for (const key of Object.keys(params).sort()) {
      search.set(key, params[key]!);
    }
    url += `?${search.toString()}`;
  }

  return url;
}
